import { useState } from 'react';
import { ArrowRight, FolderSearch, Loader2 } from 'lucide-react';
import { FieldInput } from '../ui/field';
import { PillButton } from '../ui/pill-button';
import { SourceCard } from '../ui/source-card';
import type { LoadedThread } from '../conversations/loaded-thread';
import { PastTicketCard } from '../conversations/past-ticket';

interface ConversationDetailsProps {
  loaded: LoadedThread | null;
  /** The past ticket behind the loaded scenario or the reopened run, when it has one. */
  pastTicketId: string | null;
  onOpenPicker: () => void;
  onLoadTicketId: (ticketId: string) => Promise<boolean>;
}

const TicketIdLoader = ({ onLoad }: { onLoad: (ticketId: string) => Promise<boolean> }) => {
  const [ticketId, setTicketId] = useState('');
  const [loading, setLoading] = useState(false);
  const ready = Boolean(ticketId.trim()) && !loading;

  const load = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      if (await onLoad(ticketId)) setTicketId('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative min-w-0 flex-1'>
      <FieldInput
        value={ticketId}
        onChange={(e) => setTicketId(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void load();
        }}
        placeholder='Ticket id'
        className='pr-9'
      />
      <button
        type='button'
        aria-label='Load ticket'
        disabled={!ready}
        onClick={() => void load()}
        className='absolute top-1/2 right-1 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-text-body transition-colors hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-40'
      >
        {loading ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <ArrowRight className='h-3.5 w-3.5' />}
      </button>
    </div>
  );
};

/** Where the thread came from, and the two ways to replace it: a ticket id, or browsing. */
export const ConversationDetails = ({ loaded, pastTicketId, onOpenPicker, onLoadTicketId }: ConversationDetailsProps) => (
  <div className='flex flex-col gap-3'>
    {loaded ? (
      <SourceCard
        title={loaded.contact?.name || loaded.contact?.email || 'Unknown contact'}
        meta={[
          `#${loaded.id}`,
          // Only when the name took the title, so the email is never printed twice.
          loaded.contact?.name ? loaded.contact.email : null,
          loaded.subject,
          loaded.channel,
        ]
          .filter(Boolean)
          .join(' · ')}
      />
    ) : (
      pastTicketId && <PastTicketCard ticketId={pastTicketId} />
    )}
    <div className='flex items-center gap-2'>
      <TicketIdLoader onLoad={onLoadTicketId} />
      <PillButton onClick={onOpenPicker} className='h-8 shrink-0'>
        <FolderSearch className='h-3.5 w-3.5' />
        Browse
      </PillButton>
    </div>
  </div>
);
